import { metrics } from '@cbsinteractive/avia-js-metrics';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { AviaPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/AviaPlugin';

/**
 * @copyright © Paramount Streaming
 * @description Avia metrics plugin integration for comprehensive player diagnostics
 *
 * **Diagnostic Function:**
 * This plugin integrates the official Avia metrics plugin to provide comprehensive,
 * structured diagnostic data about the player, device, network, and playback state.
 * Used by Nerd Mode and other diagnostic tools for detailed player insights.
 *
 * **Data Categories:**
 * - **Device**: Manufacturer, model, OS, DRM capabilities, HDCP support
 * - **Configuration**: Player version, adapter info, resource configuration
 * - **Performance**: CPU cores/usage, memory usage
 * - **Network**: IP address, throughput metrics, CDN information
 * - **Playback**: Content time, duration, stream time, TTFF measurements
 * - **Audio**: Track info, codec, channels, Dolby support, buffer state
 * - **Video**: Resolution, bitrate, HDR, frames, codec, buffer state
 * - **Text**: Subtitle/CC tracks and configuration
 * - **Ad**: Current ad information and pod details
 *
 * **Optional Plugin:**
 * This is classified as optional (non-critical) - failure to load will not prevent
 * video playback. Diagnostic features will gracefully degrade if unavailable.
 *
 * **Conditional Loading:**
 * Only loads when Nerd Mode is enabled in config to avoid unnecessary overhead
 * for production users who don't need diagnostic features.
 *
 * **Integration:**
 * Uses Avia's official avia-js-metrics plugin (v2.54.0+) for reliable,
 * maintained access to comprehensive player metrics.
 *
 * @see {@link https://github.com/cbsinteractive/avia-js} Avia Metrics Documentation
 */
export class MetricsPlugin extends AviaPlugin implements IAviaPlugin {
	package = metrics;
	namespaceKey = 'plugin_metrics';

	/**
	 * @description Initializes metrics plugin for Avia player diagnostic data collection
	 *
	 * **Plugin Configuration:**
	 * Sets up metrics plugin with Smart Tag namespace and Avia package integration.
	 * Provides comprehensive player diagnostics when enabled.
	 *
	 * @param {ITagAbstractClassArgument} argument Tag instance for business logic integration
	 */
	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'MetricsPlugin'
			}
		);
	}

	/**
	 * @description Determines if metrics plugin should be loaded based on Nerd Mode configuration
	 *
	 * **Conditional Loading Strategy:**
	 * This method implements performance optimization by only loading the metrics plugin
	 * when diagnostic features are enabled, avoiding unnecessary overhead in production.
	 *
	 * **Loading Conditions:**
	 *
	 * **✅ Load Metrics Plugin:**
	 * - Nerd Mode is enabled in Tag configuration
	 * - Development/QA environments requiring diagnostics
	 * - Debug mode is active for troubleshooting
	 *
	 * **❌ Skip Metrics Plugin:**
	 * - Nerd Mode is disabled (production default)
	 * - Standard user playback without diagnostic needs
	 * - Performance-sensitive environments
	 *
	 * **Performance Benefits:**
	 * - **Reduced Bundle Size**: Plugin not loaded for normal users
	 * - **Lower Memory**: No metrics data collection overhead
	 * - **Faster Initialization**: Fewer plugins to initialize
	 * - **Production Optimization**: Diagnostic features only when needed
	 *
	 * **Business Impact:**
	 * - **Developer Experience**: Full diagnostics available when needed
	 * - **User Experience**: Zero overhead for regular users
	 * - **Operational Efficiency**: Diagnostic data available for QA and support
	 *
	 * @returns {boolean} True if metrics plugin should be loaded for diagnostic features
	 */
	shouldBeUsed(): boolean {
		// Check if Nerd Mode is enabled in config
		const nerdModeEnabled = this.tagConfig?.NerdMode?.enabled ?? false;

		// Can also check for debug mode as additional condition
		const isDebugMode = this.runtimeVO?.debug?.on ?? false;

		// Load plugin if Nerd Mode enabled OR debug mode active
		return nerdModeEnabled || isDebugMode;
	}

	/**
	 * @description Provides metrics plugin configuration parameters for Avia player
	 *
	 * **Configuration Strategy:**
	 * Inherits base plugin configuration from AviaPlugin parent class and extends
	 * with metrics-specific settings for diagnostic data collection.
	 *
	 * **Configuration Options:**
	 * - **debug**: Enables verbose logging for metrics plugin (mirrors player debug flag)
	 *
	 * **Avia Integration:**
	 * Returns configuration object compatible with avia-js-metrics plugin,
	 * enabling comprehensive diagnostic data collection within the Avia player ecosystem.
	 *
	 * @returns {Record<string, any>} Metrics plugin configuration for Avia player
	 */
	getFactoryParams() {
		return {
			...super.getFactoryParams(),
			debug: this.runtimeVO?.debug?.player || false
		};
	}
}